import React, { useState } from 'react';
import './SignupPage.css';

// Suppress ResizeObserver error - this is a known React issue and doesn't affect functionality
if (typeof window !== 'undefined') {
  // Store the original error handler
  const originalError = console.error;
  
  // Override console.error to filter out ResizeObserver errors
  console.error = (...args) => {
    if (
      typeof args[0] === 'string' &&
      args[0].includes('ResizeObserver loop')
    ) {
      return; // Suppress this specific error
    }
    originalError.apply(console, args);
  };

  // Catch the error at the window level
  window.addEventListener('error', (e) => {
    if (e.message && e.message.includes('ResizeObserver loop')) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (e) => {
    if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver loop')) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  });

  // Debounce ResizeObserver to prevent the loop error
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Override ResizeObserver to add debouncing
  if (window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback) {
        const debouncedCallback = debounce(callback, 20);
        super(debouncedCallback);
      }
    };
  }
}

// Get the correct API URL based on environment
const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' 
    ? 'https://samplesphere.soave.ink/api'
    : 'http://localhost:5000/api');

const SignupPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    labelRadarLink: '',
    spotifyArtistLink: '',
    genre: '',
    subGenre: '',
    socialMediaLinks: [{ platform: 'Instagram', url: '' }],
    country: '',
    termsAccepted: false,
    marketingConsent: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [artistImage, setArtistImage] = useState('');
  const [fetchingData, setFetchingData] = useState(false);
  const [labelRadarData, setLabelRadarData] = useState(null);
  const [genres, setGenres] = useState([]);
  const [genreSearch, setGenreSearch] = useState('');
  const [showGenreDropdown, setShowGenreDropdown] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [signupSource, setSignupSource] = useState('direct');

  const socialPlatforms = ['Instagram', 'Facebook', 'Soundcloud', 'YouTube', 'TikTok', 'Twitch', 'Discord'];

  // Fetch genres from database
  const fetchGenres = async () => {
    try {
      const response = await fetch(`${API_URL}/genres`);
      const data = await response.json();
      if (data.success) {
        setGenres(data.genres || []);
      }
    } catch (error) {
      console.error('Error fetching genres:', error);
    }
  };

  // Create a new genre from LabelRadar if it doesn't exist
  const createGenreIfNotExists = async (genreName) => {
    try {
      // Check if genre already exists
      const existingGenre = genres.find(g => g.name.toLowerCase() === genreName.toLowerCase());
      if (existingGenre) {
        return existingGenre.name;
      }

      // Create new genre from LabelRadar
      const response = await fetch(`${API_URL}/genres/from-labelradar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: genreName })
      });

      const data = await response.json();
      if (data.success) {
        // Refresh genres list
        await fetchGenres();
        return data.genre.name;
      }
      return genreName;
    } catch (error) {
      console.error('Error creating genre from LabelRadar:', error);
      return genreName;
    }
  };

  // Load genres on component mount
  React.useEffect(() => {
    fetchGenres();
  }, []);

  // Capture signup source from URL and track visit
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Support standard tracking parameters: ?source=email or ?utm_source=email
    const source = urlParams.get('source') || urlParams.get('utm_source') || 'direct';
    setSignupSource(source);

    // Track the visit
    if (source !== 'direct') {
      fetch(`${API_URL}/tracking/visit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ source })
      }).catch(err => console.error('Error tracking visit:', err));
    }
  }, []);

  // Set page title and meta description for signup page
  React.useEffect(() => {
    document.title = 'Samplesphere Artist Signup';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Samplesphere Artist Signup');
    }
  }, []);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.genre-search-container')) {
        setShowGenreDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch LabelRadar data
  const fetchLabelRadarData = async (url) => {
    try {
      // Extract username from URL
      const match = url.match(/\/artists\/([^/]+)/);
      if (!match) {
        return;
      }

      const username = match[1];
      setFetchingData(true);

      // Fetch from LabelRadar API
      const response = await fetch(`https://api.labelradar.com/v1/artists/${username}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch artist data');
      }

      const data = await response.json();
      setLabelRadarData(data);

      // Auto-fill form data
      const socialLinks = [];

      // Instagram
      if (data.instagrams && data.instagrams.length > 0) {
        socialLinks.push({
          platform: 'Instagram',
          url: `https://instagram.com/${data.instagrams[0].account}`
        });
      }

      // Facebook
      if (data.facebooks && data.facebooks.length > 0) {
        socialLinks.push({
          platform: 'Facebook',
          url: `https://facebook.com/${data.facebooks[0].account}`
        });
      }

      // Soundcloud
      if (data.soundclouds && data.soundclouds.length > 0) {
        socialLinks.push({
          platform: 'Soundcloud',
          url: `https://soundcloud.com/${data.soundclouds[0].account}`
        });
      }

      // YouTube
      if (data.youtubes && data.youtubes.length > 0) {
        socialLinks.push({
          platform: 'YouTube',
          url: `https://youtube.com/${data.youtubes[0].account}`
        });
      }

      // Twitter (map to TikTok if needed, or skip)
      if (data.twitters && data.twitters.length > 0) {
        socialLinks.push({
          platform: 'TikTok',
          url: `https://twitter.com/${data.twitters[0].account}`
        });
      }

      // Get Spotify link
      let spotifyLink = '';
      if (data.spotifies && data.spotifies.length > 0) {
        spotifyLink = `https://open.spotify.com/${data.spotifies[0].account}`;
      }

      // Create genre if it doesn't exist and get the name
      let genreName = data.primaryGenre?.name || '';
      if (genreName) {
        genreName = await createGenreIfNotExists(genreName);
      }

      // Update form
      setFormData(prev => ({
        ...prev,
        name: data.fullname || prev.name,
        genre: genreName || prev.genre,
        country: data.location || prev.country,
        socialMediaLinks: socialLinks.length > 0 ? socialLinks : prev.socialMediaLinks,
        spotifyArtistLink: spotifyLink
      }));

      // Update genre search to show the selected genre
      setGenreSearch(genreName || '');

      // Set artist image
      if (data.imageUrl) {
        setArtistImage(data.imageUrl);
      }

    } catch (error) {
      console.error('Error fetching LabelRadar data:', error);
      setMessage('Could not fetch artist data. Please fill manually.');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setFetchingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // If LabelRadar link is being pasted, fetch data
    if (name === 'labelRadarLink' && value.includes('labelradar.com/artists/')) {
      fetchLabelRadarData(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSocialMediaChange = (index, field, value) => {
    const newSocialLinks = [...formData.socialMediaLinks];
    newSocialLinks[index][field] = value;
    setFormData(prev => ({
      ...prev,
      socialMediaLinks: newSocialLinks
    }));
  };

  const addSocialMediaLink = () => {
    setFormData(prev => ({
      ...prev,
      socialMediaLinks: [...prev.socialMediaLinks, { platform: 'Instagram', url: '' }]
    }));
  };

  const removeSocialMediaLink = (index) => {
    const newSocialLinks = formData.socialMediaLinks.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      socialMediaLinks: newSocialLinks.length > 0 ? newSocialLinks : [{ platform: 'Instagram', url: '' }]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    // Validate required fields
    if (!formData.name || !formData.email) {
      setMessage('Please fill in your name and email');
      setLoading(false);
      return;
    }

    if (!formData.genre || !formData.country) {
      setMessage('Please select both genre and country');
      setLoading(false);
      return;
    }

    if (!formData.termsAccepted) {
      setMessage('Please accept the terms and conditions');
      setLoading(false);
      return;
    }

    // Filter out empty social media links
    const filteredSocialLinks = formData.socialMediaLinks.filter(link => link.url.trim() !== '');

    try {
      const response = await fetch(`${API_URL}/subscribers/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          socialMediaLinks: filteredSocialLinks,
          artistImage,
          labelRadarData: labelRadarData, // Store full LabelRadar data
          signupSource: signupSource // Track signup source
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setMessage('Thank you for signing up! Welcome to our community.');
        
        // Track signup conversion with Reddit Pixel
        if (window.rdt) {
          window.rdt('track', 'SignUp');
        }
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          labelRadarLink: '',
          spotifyArtistLink: '',
          genre: '',
          subGenre: '',
          socialMediaLinks: [{ platform: 'Instagram', url: '' }],
          country: '',
          termsAccepted: false,
          marketingConsent: false
        });
      } else {
        setMessage(data.message || 'Error signing up. Please try again.');
      }
    } catch (error) {
      console.error('Signup error:', error);
      setMessage('Error connecting to server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-split-container">
        {/* Left Side - Information */}
        <div className="signup-left-side">
          {/* Logo in top left corner */}
          <div className="left-logo">
            <img 
              src="https://samplesphere.com/cdn/shop/files/Logo.png" 
              alt="SampleSphere" 
              className="logo"
            />
          </div>

          {/* Banner underneath logo */}
          <div className="banner-section">
            <img 
              src="https://soaveyoutubebackup.s3.us-east-1.amazonaws.com/Mb0eIc8Q.png" 
              alt="Sign up & get free samples every day" 
              className="banner-image"
            />
          </div>

          <div className="left-content">
            {/* Welcome Text */}
            <div className="welcome-section">
              <h3 className="welcome-subtitle">JOIN OUR COMMUNITY</h3>
              <h2 className="welcome-title">Sign up and receive a free royalty-free sample every day!</h2>
              
            </div>

            {/* Advent Calendar Information */}
            <div className="advent-info-section">
              {/* Highlighted Description Box */}
              <div className="advent-description-box">
                <h2 className="advent-box-heading">🎄 Advent Calendar 🎄</h2>
                <ul className="advent-description-list">
                  <li>
                    From December 1st until December 24th, you receive a special prize after every 4 day streak besides the free sample. No streak = only sample, no additional prize
                  </li>
                  <li>
                    People with a 24 day streak between Dec 1 - Dec 24, receive a special prize at the end of the campaign
                  </li>
                </ul>
              </div>

              {/* Divider */}
              <div className="advent-divider"></div>

              <div className="advent-offers-list">
                <div className="advent-offer-item">
                  <div className="card-content">
                    <div className="offer-date">Prize</div>
                    <div className="card-icon">🎁</div>
                    <div className="hover-prompt">Hover me</div>
                    <div className="offer-text">
                      <div className="offer-title">Prize</div>
                      <div>10% discount on a music marketing campaign with YouGrow</div>
                    </div>
                  </div>
                </div>
                <div className="advent-offer-item">
                  <div className="card-content">
                    <div className="offer-date">Prize</div>
                    <div className="card-icon">🎁</div>
                    <div className="hover-prompt">Hover me</div>
                    <div className="offer-text">
                      <div className="offer-title">Prize</div>
                      <div>Get 50% off your Soave webshop purchase until December 31st, 2025</div>
                    </div>
                  </div>
                </div>
                <div className="advent-offer-item">
                  <div className="card-content">
                    <div className="offer-date">Prize</div>
                    <div className="card-icon">🎁</div>
                    <div className="hover-prompt">Hover me</div>
                    <div className="offer-text">
                      <div className="offer-title">Prize</div>
                      <div>50% off annual membership + 5 free playlist pitches with un:hurd</div>
                    </div>
                  </div>
                </div>
                <div className="advent-offer-item">
                  <div className="card-content">
                    <div className="offer-date">Prize</div>
                    <div className="card-icon">🎁</div>
                    <div className="hover-prompt">Hover me</div>
                    <div className="offer-text">
                      <div className="offer-title">Prize</div>
                      <div>€25 off a €100,- TikTok campaign with MUVID</div>
                    </div>
                  </div>
                </div>
                <div className="advent-offer-item">
                  <div className="card-content">
                    <div className="offer-date">Prize</div>
                    <div className="card-icon">🎁</div>
                    <div className="hover-prompt">Hover me</div>
                    <div className="offer-text">
                      <div className="offer-title">Prize</div>
                      <div>10% discount on credits for a playlist campaign through Submithub</div>
                    </div>
                  </div>
                </div>
                <div className="advent-offer-item mystery-gift">
                  <div className="card-content">
                    <div className="offer-date">DEC 25</div>
                    <div className="card-icon">🎁</div>
                    <div className="hover-prompt">Hover me</div>
                    <div className="offer-text mystery-text">
                      <div className="offer-title">DEC 25</div>
                      <div className="mystery-icon">🪅</div>
                      <div>Special surprise for a full 24 day streak!!</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="signup-right-side">
          <div className="form-container">

            {/* Form Section */}
            {success ? (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h2>Successfully Signed Up!</h2>
                <p>{message}</p>
                <p className="success-details">
                  We've received your application. You'll start receiving exclusive samples soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="signup-form">
                {message && !success && (
                  <div className="error-message">
                    {message}
                  </div>
                )}

                {/* Form Row for First Name and Last Name */}
                

                {/* Widget 1: Basic Information */}
                <div className="form-widget">
                  <h3 className="widget-title">Basic Information</h3>
                
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your name"
                    required
                  />
                </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                <div className="form-group">
                  <label htmlFor="labelRadarLink">LabelRadar/ Spotify (Autofill the rest with LabelRadar link)</label>
                  <input
                    type="url"
                    id="labelRadarLink"
                    name="labelRadarLink"
                    value={formData.labelRadarLink}
                    onChange={handleInputChange}
                    placeholder="https://labelradar.com/... or https://open.spotify.com/artist/..."
                  />
                </div>

                {fetchingData && (
                  <div className="fetching-message">
                    🔄 Fetching artist data...
                  </div>
                )}

                {artistImage && !fetchingData && (
                  <div className="success-fetch-message">
                    ✓ Artist data loaded successfully!
                  </div>
                )}
                
                {/* Artist Image */}
                {artistImage && (
                  <div className="artist-image-container">
                    <img src={artistImage} alt="Artist" className="artist-image" />
                  </div>
                )}

                  
                </div>

                {/* Widget 2: Genre & Additional Details */}
                <div className="form-widget">
                  <h3 className="widget-title">Genre & Additional Details</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="genre">Genre *</label>
                    <div className="genre-search-container">
                      <input
                        type="text"
                        id="genre"
                        name="genre"
                        value={genreSearch}
                        onChange={(e) => {
                          setGenreSearch(e.target.value);
                          setShowGenreDropdown(true);
                          // Only update formData if the genre exists in the list
                          const matchingGenre = genres.find(g => 
                            g.name.toLowerCase() === e.target.value.toLowerCase()
                          );
                          if (matchingGenre) {
                            setFormData(prev => ({ ...prev, genre: matchingGenre.name }));
                          } else {
                            setFormData(prev => ({ ...prev, genre: '' }));
                          }
                        }}
                        onFocus={() => setShowGenreDropdown(true)}
                        placeholder="Search genres..."
                        required
                        autoComplete="off"
                      />
                      {showGenreDropdown && (
                        <div className="genre-dropdown">
                          {genres
                            .filter(g => 
                              g.name.toLowerCase().includes(genreSearch.toLowerCase())
                            )
                            .slice(0, 10)
                            .map(genre => (
                              <div
                                key={genre._id}
                                className="genre-option"
                                onClick={() => {
                                  setGenreSearch(genre.name);
                                  setFormData(prev => ({ ...prev, genre: genre.name }));
                                  setShowGenreDropdown(false);
                                }}
                              >
                                {genre.name}
                              </div>
                            ))}
                          {genres.filter(g => g.name.toLowerCase().includes(genreSearch.toLowerCase())).length === 0 && genreSearch && (
                            <div className="genre-option-empty">
                              No matching genres found. Use LabelRadar link to add new genres.
                            </div>
                          )}
                          {genres.filter(g => g.name.toLowerCase().includes(genreSearch.toLowerCase())).length === 0 && !genreSearch && (
                            <div className="genre-option-empty">
                              Start typing to search genres...
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {genreSearch && !formData.genre && (
                      <div className="genre-warning">
                        ⚠️ Please select a genre from the list or use LabelRadar link
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    
                  </div>
                </div>

                <div className="form-group">
                  <label>Social Media Links</label>
                  {formData.socialMediaLinks.map((link, index) => (
                    <div key={index} className="social-link-group">
                      <select
                        value={link.platform}
                        onChange={(e) => handleSocialMediaChange(index, 'platform', e.target.value)}
                        className="platform-select"
                      >
                        {socialPlatforms.map(platform => (
                          <option key={platform} value={platform}>{platform}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={link.url}
                        onChange={(e) => handleSocialMediaChange(index, 'url', e.target.value)}
                        placeholder={`Enter your ${link.platform} Username or URL`}
                        className="social-url-input"
                      />
                      {formData.socialMediaLinks.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSocialMediaLink(index)}
                          className="remove-social-btn"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addSocialMediaLink}
                    className="add-social-btn"
                  >
                    + Add Another Social Link
                  </button>
                </div>

                <div className="form-group">
                  <label htmlFor="country">Country *</label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Enter your country"
                      required
                    />
                  </div>
                </div>

                {/* Terms & Submit */}
                <div className="form-footer">
                  <div className="terms-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="termsAccepted"
                        checked={formData.termsAccepted}
                        onChange={handleInputChange}
                        required
                      />
                      <span>
                        I accept the{' '}
                        <span 
                          className="terms-link" 
                          onClick={(e) => {
                            e.preventDefault();
                            setShowTermsModal(true);
                          }}
                        >
                          Terms & Conditions
                        </span>
                        {' '}*
                      </span>
                    </label>
                    
                    
                  </div>

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'SUBMITTING...' : 'Submit'}
                  </button>

                  
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      {showTermsModal && (
          <div className="modal-overlay" onClick={() => setShowTermsModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Terms & Conditions</h2>
                <button 
                  className="modal-close" 
                  onClick={() => setShowTermsModal(false)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <h3>TERMS OF SERVICE & PRIVACY NOTICE</h3>
                <p><strong>Last updated: 21/11/2025</strong></p>
                
                <p>These Terms of Service ("Terms") and Privacy Notice ("Notice") explain how Soave B.V. ("Soave") and Samplesphere ("Samplesphere") process your personal data in connection with the Soave Producer Community and related promotional activities, including the daily-sample campaign.</p>
                
                <p>By submitting your information through the sign-up form hosted by Samplesphere, you acknowledge that you have read and understood these Terms and agree to them.</p>
                
                <p><strong>If you do not agree, please do not sign up.</strong></p>
                
                <h4>1. Identity of the Parties</h4>
                <p><strong>Soave B.V.</strong><br/>
                Wijnstraat 96 F<br/>
                3011 TR Rotterdam<br/>
                The Netherlands<br/>
                hello@soaverecords.com</p>
                
                <p><strong>Samplesphere</strong><br/>
                De Beuk 40, 2912NB, Nieuwerkerk aan den IJssel<br/>
                info@samplesphere.com</p>
                
                <h4>2. Purpose of the Campaign</h4>
                <p>The campaign allows you to:</p>
                <ul>
                  <li>receive daily emails containing 24-hour download links to exclusive samples;</li>
                  <li>join Soave's Producer Community;</li>
                  <li>receive marketing communications from both Soave and Samplesphere;</li>
                  <li>receive invitations to contests, events, and product releases;</li>
                  <li>benefit from personalized content and advertising based on your preferences and behaviour.</li>
                </ul>
                
                <h4>3. Personal Data Processed</h4>
                <p><strong>3.1 Data Provided Directly by You</strong></p>
                <ul>
                  <li>Name</li>
                  <li>Email address</li>
                  <li>LabelRadar or Spotify profile links</li>
                  <li>Genre and sub-genre preferences</li>
                  <li>Social media links (Instagram, Facebook, SoundCloud, YouTube, TikTok, Twitch, Discord)</li>
                  <li>Country or location</li>
                  <li>Artist profile images</li>
                  <li>Full LabelRadar profile data (if provided)</li>
                </ul>
                
                <p><strong>3.2 Data Collected Automatically (Cookies, Pixels, Tracking)</strong></p>
                <ul>
                  <li>IP address</li>
                  <li>Location derived from IP</li>
                  <li>Browser type/version</li>
                  <li>Device information</li>
                  <li>Pages visited and time spent</li>
                  <li>Source/referrer</li>
                  <li>Cookie identifiers and similar technologies</li>
                </ul>
                
                <p>Tracking tools include:</p>
                <ul>
                  <li>Reddit Pixel</li>
                  <li>Meta (Facebook/Instagram) Pixel</li>
                  <li>Session/analytics cookies</li>
                </ul>
                
                <h4>4. Purposes and Legal Bases for Processing</h4>
                <p><strong>4.1 Consent</strong></p>
                <p>We process your data for:</p>
                <ul>
                  <li>sending marketing emails from Soave</li>
                  <li>sending marketing emails from Samplesphere</li>
                  <li>delivering daily sample packs</li>
                  <li>targeted advertising (Meta/Reddit custom audiences)</li>
                  <li>behaviour-based analytics and retargeting</li>
                  <li>using cookies/pixels for tracking and marketing</li>
                  <li>collecting full LabelRadar profile data</li>
                </ul>
                
                <p>You may withdraw your consent at any time (see Section 10).</p>
                
                <p><strong>4.2 Legitimate Interests</strong></p>
                <p>We process your data to:</p>
                <ul>
                  <li>maintain and improve our platforms</li>
                  <li>ensure cybersecurity and fraud prevention</li>
                  <li>measure campaign effectiveness</li>
                  <li>perform aggregated analytics</li>
                </ul>
                
                <p>Our legitimate interests do not override your rights and freedoms. You may object to such processing.</p>
                
                <h4>5. Marketing Communications</h4>
                <p>By signing up:</p>
                <ul>
                  <li>you will receive communications from both Soave and Samplesphere;</li>
                  <li>you understand that these are separate marketing lists with independent unsubscribe options;</li>
                  <li>unsubscribing from one does not automatically unsubscribe you from the other.</li>
                </ul>
                
                <h4>6. Recipients of Personal Data</h4>
                <p><strong>6.1 Technical Processors</strong></p>
                <p>Processed strictly under Article 28 GDPR:</p>
                <ul>
                  <li>AWS SES – email delivery</li>
                  <li>MongoDB – encrypted data storage</li>
                  <li>AWS – cloud infrastructure</li>
                  <li>Railway – hosting services</li>
                </ul>
                
                <p><strong>6.2 Advertising and Analytics Providers</strong></p>
                <p>Under your consent:</p>
                <ul>
                  <li>Reddit (Reddit Pixel)</li>
                  <li>Meta (Facebook/Instagram Pixel)</li>
                </ul>
                
                <p>We do not sell personal data.</p>
                
                <p><strong>6.3 LabelRadar Uploaders</strong></p>
                <p>If you previously submitted a demo to LabelRadar, and provided marketing consent, your existing data may be used for this campaign.</p>
                
                <h4>7. International Data Transfers</h4>
                <p>If any data is transferred outside the EEA (e.g., to the United States for infrastructure or advertising platforms), this is done using:</p>
                <ul>
                  <li>the European Commission Standard Contractual Clauses (SCCs), and</li>
                  <li>supplementary measures where required.</li>
                </ul>
                <p>You may request a copy of the relevant safeguards.</p>
                
                <h4>8. Cookies & Tracking Technologies</h4>
                <p>We use cookies for:</p>
                <p><strong>8.1 Essential Purposes</strong> – Necessary for website functionality.</p>
                <p><strong>8.2 Analytics & Performance</strong> – Measuring usage and improving services.</p>
                <p><strong>8.3 Marketing & Retargeting</strong> – Meta and Reddit Pixels for audience building, behavioural analysis, conversion tracking.</p>
                
                <p>Your explicit consent is required before placing any non-essential cookies or pixels. You may withdraw consent at any time or change cookie preferences through your browser.</p>
                
                <h4>9. Data Retention</h4>
                <p>We retain personal data only as long as necessary for delivering samples, community membership, marketing, analytics, advertising, and legal compliance.</p>
                <ul>
                  <li>Marketing data: until you withdraw consent</li>
                  <li>Cookie/pixel data: according to the cookie provider's policies</li>
                  <li>Server logs: up to 12 months</li>
                </ul>
                <p>Data may be anonymised for statistical purposes.</p>
                
                <h4>10. Your Rights</h4>
                <p>You have the right to:</p>
                <ul>
                  <li>access your personal data</li>
                  <li>rectify inaccurate data</li>
                  <li>erase your data ("right to be forgotten")</li>
                  <li>withdraw consent at any time</li>
                  <li>object to marketing and legitimate-interest processing</li>
                  <li>restrict processing in certain cases</li>
                  <li>data portability (obtain a copy in machine-readable form)</li>
                  <li>lodge a complaint with a supervisory authority (Netherlands: Autoriteit Persoonsgegevens)</li>
                </ul>
                <p>To exercise rights, contact: info@samplesphere.com</p>
                
                <h4>11. Children</h4>
                <p>The services are not intended for individuals under 16 years old. We do not knowingly collect children's data.</p>
                
                <h4>12. Security Measures</h4>
                <p>We use appropriate technical and organisational measures including encrypted storage, secured cloud hosting, access control, monitoring, and vulnerability assessments.</p>
                <p>No system is completely secure.</p>
                
                <h4>13. Changes to These Terms</h4>
                <p>We may update these Terms and/or Notice from time to time. Significant changes will be communicated via email or posted on the registration page.</p>
                
                <h4>14. Contact</h4>
                <p><strong>Soave</strong><br/>
                Email: hello@soaverecords.com</p>
                
                <p>For questions or requests regarding these Terms or Notice:</p>
                <p><strong>Samplesphere</strong><br/>
                Email: info@samplesphere.com</p>
              </div>
              <div className="modal-footer">
                <button 
                  className="modal-close-button" 
                  onClick={() => setShowTermsModal(false)}
                >
                  Close
                </button>
              </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="signup-footer">
        <p>© 2025 Soave Music Group. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default SignupPage;

